import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/common/PageLayout';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { StatusBadge } from '../components/common/StatusBadge';
import { HeroCard } from '../components/dashboard/HeroCard';
import { StepCard } from '../components/dashboard/StepCard';
import { StartRunModal } from '../components/dashboard/StartRunModal';
import { useDashboardData, ProcessRun } from '../hooks/useCaapApi';

function formatCurrency(value: number | null): string {
  if (value == null) return '\u2014';
  return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2 });
}

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useDashboardData();
  const [showStartRun, setShowStartRun] = useState(false);

  const step7 = data?.stepSummaries.find((s) => s.stepNumber === 7);
  const steps1to6 = data?.stepSummaries.filter((s) => s.stepNumber >= 1 && s.stepNumber <= 6) ?? [];

  return (
    <PageLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary">Collections Dashboard</h1>
        <p className="mt-1 text-sm text-muted">
          Trailing 12-month overview of automated collections process
        </p>
      </div>

      {isLoading && <LoadingSpinner message="Loading dashboard..." />}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-950 p-4 text-sm text-red-700 dark:text-red-300">
          Failed to load dashboard: {(error as Error).message}
        </div>
      )}

      {data && (
        <>
          {/* Hero Card — Step 7 */}
          <HeroCard months={step7?.months ?? []} />

          {/* Step Cards — Steps 1-6 */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
            {[1, 2, 3, 4, 5, 6].map((stepNum) => {
              const stepData = steps1to6.find((s) => s.stepNumber === stepNum);
              return (
                <StepCard
                  key={stepNum}
                  stepNumber={stepNum}
                  stepName={stepData?.stepName ?? `Step ${stepNum}`}
                  months={stepData?.months ?? []}
                />
              );
            })}
          </div>

          {/* Recent Runs Table */}
          <div className="bg-surface-alt rounded-lg shadow-sm border border-border-default">
            <div className="px-6 py-4 border-b border-border-default flex items-center justify-between">
              <h2 className="text-lg font-semibold text-primary">Recent Runs</h2>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowStartRun(true)}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Start New Run
                </button>
                <button
                  onClick={() => navigate('/caap')}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  View All Runs &rarr;
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-surface text-left text-xs font-medium text-muted uppercase tracking-wider">
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Type</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Accounts Found</th>
                    <th className="px-6 py-3 text-right">Assigned</th>
                    <th className="px-6 py-3 text-right">Payoff Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-default">
                  {data.recentRuns.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-sm text-muted">
                        No runs yet
                      </td>
                    </tr>
                  ) : (
                    data.recentRuns.map((run: ProcessRun) => (
                      <tr
                        key={run.RunID}
                        className="hover:bg-surface-hover cursor-pointer"
                        onClick={() => navigate(`/caap/runs/${run.RunID}`)}
                      >
                        <td className="px-6 py-3 text-sm text-primary">
                          {new Date(run.RunDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-3 text-sm text-secondary">{run.ScheduleType}</td>
                        <td className="px-6 py-3">
                          <StatusBadge status={run.Status} />
                        </td>
                        <td className="px-6 py-3 text-sm text-primary text-right">
                          {run.TotalAccountsFound}
                        </td>
                        <td className="px-6 py-3 text-sm text-primary text-right">
                          {run.TotalAccountsAssigned > 0 ? run.TotalAccountsAssigned : '\u2014'}
                        </td>
                        <td className="px-6 py-3 text-sm text-primary text-right">
                          {formatCurrency(run.TotalPayoffBalance)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <StartRunModal
        open={showStartRun}
        onClose={() => setShowStartRun(false)}
        onRunStarted={(runId) => {
          setShowStartRun(false);
          navigate(`/caap/runs/${runId}`);
        }}
      />
    </PageLayout>
  );
};
